/**
 * Reorders private booking rounds to ensure correct index assignment:
 * - Owner at index 0
 * - All guest rounds (guest + unknownGuest) immediately after
 * - All new guest rounds after guest rounds
 * - Remaining member rounds after all guests
 *
 * This ensures guests are correctly associated with the booking owner.
 *
 * Used during the patron-app booking CREATE flow (single-tee-time wizard and
 * multi-tee-time draft creation). Not used when editing an already-confirmed
 * booking — there the user-specified host assignments must be preserved.
 */

type RoundWithIndex = { index?: number | null; playerId?: string | null };

interface ReorderPrivateRoundsParams<
  TMember extends RoundWithIndex,
  TGuest extends RoundWithIndex,
  TNewGuest extends RoundWithIndex
> {
  memberRounds: TMember[];
  guestRounds: TGuest[];
  newGuestRounds: TNewGuest[];
  ownerClientId: string;
}

interface ReorderedPrivateRounds<
  TMember extends RoundWithIndex,
  TGuest extends RoundWithIndex,
  TNewGuest extends RoundWithIndex
> {
  memberRounds: TMember[];
  guestRounds: TGuest[];
  newGuestRounds: TNewGuest[];
}

export const reorderPrivateRounds = <
  TMember extends RoundWithIndex,
  TGuest extends RoundWithIndex,
  TNewGuest extends RoundWithIndex
>({
  memberRounds,
  guestRounds,
  newGuestRounds,
  ownerClientId,
}: ReorderPrivateRoundsParams<
  TMember,
  TGuest,
  TNewGuest
>): ReorderedPrivateRounds<TMember, TGuest, TNewGuest> => {
  // Identify the owner round. Prefer the explicit owner; fall back to the
  // first named member (one that has a playerId). A Member Hold — a member
  // round with playerId `null` — must never be treated as the owner, because
  // the backend rejects bookings where a Member Hold is the inferred host of
  // a guest (`member_hold_host` validation).
  const ownerRound =
    memberRounds.find((r) => r.playerId === ownerClientId) ??
    memberRounds.find((r) => !!r.playerId);
  const otherMemberRounds = memberRounds.filter((round) => {
    if (ownerRound == null) {
      return true;
    }

    return round !== ownerRound;
  });

  // Assign indices: owner (0), then guests, then newGuests, then other members
  let index = 0;

  const ownerWithIndex =
    ownerRound != null ? [{ ...ownerRound, index: index++ }] : [];
  const guestRoundsWithIndex = guestRounds.map((r) => ({
    ...r,
    index: index++,
  }));
  const newGuestRoundsWithIndex = newGuestRounds.map((r) => ({
    ...r,
    index: index++,
  }));
  const otherMemberRoundsWithIndex = otherMemberRounds.map((r) => ({
    ...r,
    index: index++,
  }));

  return {
    memberRounds: [...ownerWithIndex, ...otherMemberRoundsWithIndex],
    guestRounds: guestRoundsWithIndex,
    newGuestRounds: newGuestRoundsWithIndex,
  };
};
